import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

function ContentMappingTab({ project, onEdit }) {
  const { t } = useTranslation("members");

  const producers = (project?.users || []).filter(
    (u) => u.role === "producer" || u.role === "co_producer",
  );

  const getAssetTypeStyle = (type) => {
    switch (type) {
      case "series":
        return {
          backgroundColor: "#EDE9FE",
          color: "#7C3AED",
          borderColor: "#C4B5FD",
        };
      case "episode":
        return {
          backgroundColor: "#DBEAFE",
          color: "#2563EB",
          borderColor: "#93C5FD",
        };
      default:
        return {};
    }
  };

  const groupAssets = (assets) => {
    const series = assets.filter((a) => a.asset__asset_type === "series");
    const episodes = assets.filter((a) => a.asset__asset_type === "episode");
    const standalone = assets.filter(
      (a) =>
        a.asset__asset_type !== "series" && a.asset__asset_type !== "episode",
    );
    return { series, episodes, standalone };
  };

  if (producers.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 3 }}>
        {t("contentMapping.noProducers")}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("contentMapping.columns.producer")}</TableCell>
            <TableCell>{t("contentMapping.columns.role")}</TableCell>
            <TableCell>{t("contentMapping.columns.assignedAssets")}</TableCell>
            <TableCell align="right">
              {t("contentMapping.columns.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {producers.map((user) => {
            const assets = user.accessible_assets || [];
            const { series, episodes, standalone } = groupAssets(assets);

            return (
              <TableRow key={user.id}>
                <TableCell sx={{ fontWeight: 500 }}>
                  {user.user_details?.name || t("table.unknownUser")}
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`roles.${user.role}`)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {series.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {series.map((a) => (
                          <Chip
                            key={a.asset_id}
                            label={a.asset__title}
                            size="small"
                            variant="outlined"
                            sx={getAssetTypeStyle("series")}
                          />
                        ))}
                      </Box>
                    )}
                    {standalone.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {standalone.map((a) => (
                          <Chip
                            key={a.asset_id}
                            label={a.asset__title}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    {episodes.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {episodes.map((a) => (
                          <Chip
                            key={a.asset_id}
                            label={a.asset__title}
                            size="small"
                            variant="outlined"
                            sx={getAssetTypeStyle("episode")}
                          />
                        ))}
                      </Box>
                    )}
                    {assets.length === 0 && (
                      <Typography variant="bodySm" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onEdit(user)}>
                    <Pencil size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ContentMappingTab;
